import { useEffect } from 'react';
import { Outlet } from 'react-router-dom';
import { Sidebar } from '@/components/nav/sidebar';
import { MobileTopBar } from '@/components/nav/mobile-top-bar';
import { GradientBackdrop } from '@/components/shell/gradient-backdrop';
import { Drawer } from '@/components/ui/drawer';
import { AuthProvider } from '@/context/auth';
import { ConnectRepoProvider } from '@/context/connect-repo';
import { CommandPaletteProvider } from '@/context/command-palette';
import {
  AppShellDrawerProvider,
  useAppShellDrawer,
} from '@/context/app-shell-drawer';
import { useViewportBreakpoint } from '@/hooks/use-viewport-breakpoint';
import { useRestorePendingDestination } from '@/hooks/use-restore-pending-destination';
import { ConnectRepositoryModal } from '@/components/connect/connect-repository-modal';
import { CommandPalette } from '@/components/search/command-palette';

/**
 * Persistent portal shell — gradient cloud backdrop + responsive navigation +
 * scrollable main column. Mirrors the "flush" shell from nmblr.foundation.app:
 * the sidebar is a solid `--c-sidebar` island, the gradient shows behind
 * transparent page content, and cards are solid islands on top.
 *
 * This is the authenticated `AppShell` subtree (RFC-0009 §2 mount contract).
 * `AuthProvider` mounts here — and only here — so both the `GET /api/me`
 * bootstrap and the 401 interceptor run within the shell and never on /login
 * (which App.tsx renders OUTSIDE this layout). Nesting per the RFC:
 *   QueryClientProvider (main.tsx) → AuthProvider (this shell's provider slot)
 *   → routes. Later cross-cutting providers (RFC-0011's command palette,
 *   RFC-0014's app-shell drawer) mount into the same slot.
 *
 * Responsive shell (RFC-0014 §1): at `md`+ the inline <Sidebar> is visible
 * exactly as before; below `md` it is hidden (`hidden md:flex`) and its content
 * is reachable through an off-canvas <Drawer> opened by the <MobileTopBar>
 * hamburger. The drawer machinery (scrim, focus-trap, scroll-lock) is only
 * mounted below `md` — desktop renders identically to before.
 */
export function PortalLayout() {
  return (
    <div className="relative flex h-screen">
      {/* Skip-to-content link (RFC-0010 §11 / PD-0008 G22) — first focusable
          element, lets keyboard users bypass the sidebar + doc tree. */}
      <a href="#main-content" className="skip-link">
        Skip to content
      </a>
      <GradientBackdrop />
      <AuthProvider>
        {/* Gap A (RFC-0015): restore a captured URL fragment once the shell
            bootstrap resolves authenticated. Mounted inside the AuthProvider
            subtree (like ShellDrawer) so it runs only in the authenticated shell;
            renders nothing. AuthProvider mount location is unchanged. */}
        <RestorePendingDestination />
        {/* Inline desktop sidebar — visible at md+, hidden below md. */}
        <Sidebar />
        <AppShellDrawerProvider>
          {/* Column: mobile top bar stacks above the scrollable main region.
              At md+ the top bar is display:none (md:hidden), so this collapses
              to just <main>, matching the pre-RFC layout. */}
          <div className="flex min-w-0 flex-1 flex-col">
            <MobileTopBar />
            <ConnectRepoProvider>
              <CommandPaletteProvider>
                <main
                  id="main-content"
                  tabIndex={-1}
                  className="relative z-10 flex-1 overflow-auto min-w-0 p-6 md:p-8 lg:p-12 focus:outline-none"
                >
                  <div className="animate-fade-in mx-auto max-w-5xl">
                    <Outlet />
                  </div>
                </main>
                <ConnectRepositoryModal />
                <CommandPalette />
              </CommandPaletteProvider>
            </ConnectRepoProvider>
          </div>
          {/* Off-canvas navigation drawer — below md only. */}
          <ShellDrawer />
        </AppShellDrawerProvider>
      </AuthProvider>
    </div>
  );
}

/**
 * Small shell-scoped component (like ShellDrawer) that runs the Gap A fragment
 * restore hook inside the AuthProvider subtree and renders nothing.
 */
function RestorePendingDestination() {
  useRestorePendingDestination();
  return null;
}

/**
 * The off-canvas navigation drawer, mounted ONLY below `md`. At `md`+ no drawer
 * machinery (scrim, focus-trap, body scroll-lock) exists at all — the inline
 * <Sidebar> handles navigation. Resizing up to `md`+ while the drawer is open
 * auto-closes it so no stale open state lingers.
 */
function ShellDrawer() {
  const { isBelowMd } = useViewportBreakpoint();
  const { isOpen, close } = useAppShellDrawer();

  useEffect(() => {
    if (!isBelowMd && isOpen) close();
  }, [isBelowMd, isOpen, close]);

  if (!isBelowMd) return null;

  return (
    <Drawer isOpen={isOpen} onClose={close} label="Navigation" side="left">
      <Sidebar inDrawer />
    </Drawer>
  );
}
